import React from "react";
import styled from "styled-components";
import { I18n } from "@lingui/react";
import {
  Thirds,
  Left,
  Right,
  DisplayMedium,
  DisplaySmall,
  Back,
  spacing,
  color,
  DualTimeFramePicker,
} from "@conserveit/blocks";
import { TimeFrame, CompareTo } from "../../../../../context/reports/types";
import { getTimeFrameList } from "../../../utils";
import { setPersisted } from "../../../../../utils/storage";
import {
  TIMEFRAME_PERSIST_PROP,
  COMPARETO_PERSIST_PROP,
} from "../../../../../context/reports/constants";
import { navigate } from "../../../../../utils/routes/navigate";
import { Wrap } from "../../../../Layout/Page";
import { getCompareTolist } from "../../utils";

export interface ValuePair {
  label: string | number | React.ComponentType<any>;
  value: string | number;
}

const SubTitle = styled(DisplaySmall)`
  color: ${color.theme.typography.subdued};
  margin-bottom: ${spacing.extraTight};
`;

const StyledThirds = styled(Thirds)`
  ${Left} {
    flex-direction: column;
  }
`;

const HeadWrap = styled(Wrap)`
  padding: ${spacing.default} ${spacing.extraLoose};
`;

const GoBack = styled(Back)`
  margin-bottom: ${spacing.extraTight};
`;
interface Props {
  subTitle?: string;
  groupPrefix?: string;
  groupName?: string;
  showBackTo?: boolean;
  backTo?: string;
  defaultTimeFrame: TimeFrame;
  defaultCompareTo: CompareTo;
  onTimeFrameChange(timeFrame: TimeFrame, compareTo: CompareTo): void;
  persistKey: string;
}

export const HeadBar: React.FC<Props> = ({
  subTitle,
  groupPrefix,
  groupName,
  defaultTimeFrame,
  defaultCompareTo,
  onTimeFrameChange,
  backTo,
  persistKey,
}) => {
  const handleTimeFrame = React.useCallback(
    (timeFrame: ValuePair, compareTo: ValuePair) => {
      // Persist selected option in session storage
      setPersisted(
        { prop: TIMEFRAME_PERSIST_PROP, key: persistKey },
        timeFrame.value
      );
      setPersisted(
        { prop: COMPARETO_PERSIST_PROP, key: persistKey },
        compareTo.value
      );

      onTimeFrameChange(
        timeFrame.value as TimeFrame,
        compareTo.value as CompareTo
      );
    },
    []
  );

  return (
    <I18n>
      {({ i18n }) => {
        const timeFrameOptions = getTimeFrameList(i18n);
        const compareToOptions = getCompareTolist(i18n);
        const defaultTimeFrameOption =
          timeFrameOptions.find(
            (option) => option.value === defaultTimeFrame
          ) || timeFrameOptions[0];
        const defaultCompareToOption =
          compareToOptions.find(
            (option) => option.value === defaultCompareTo
          ) || compareToOptions[0];

        return (
          <HeadWrap>
            <StyledThirds>
              <Left>
                {backTo && (
                  <GoBack text="back to Previous" onClick={navigate(backTo)} />
                )}
                {subTitle && <SubTitle>{subTitle}</SubTitle>}
                <DisplayMedium>
                  {groupPrefix ? `${groupPrefix}: ` : ""}
                  {groupName ? groupName : " Portfolio view"}
                </DisplayMedium>
              </Left>
              <Right>
                <DualTimeFramePicker
                  defaultTimeFrame={defaultTimeFrameOption}
                  defaultCompareTo={defaultCompareToOption}
                  timeFrameOptions={timeFrameOptions}
                  compareToOptions={compareToOptions}
                  onApply={handleTimeFrame}
                />
              </Right>
            </StyledThirds>
          </HeadWrap>
        );
      }}
    </I18n>
  );
};
